import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../services/app-context";
import { setUser, setRefreshToken } from "../../../services/utils";
import AWS from "aws-sdk";
import * as animationData from "../../../assets/loading-bar.json";
import Lottie from "lottie-react";
import Modal from "../../../components/Modal";
import { CohortForm } from "../CohortForm";
import { api } from "../../../services/api";
import { ShowGenericErrorToast, ShowToast } from "../../../services/toast";
import { REGION } from "../../../env";
import { useNavigateToDashboard } from "../useNavigateToDashboard";
import {
  authApi,
  clearLegacyCognitoSession,
  establishBackendSession,
} from "../../../services/authApi";
import { isBackendAuthEnabled } from "../../../integrations/posthog/featureFlags";
import { getLoggerRef } from "../../../integrations/logger/logger-bridge";

const cognito = new AWS.CognitoIdentityServiceProvider({
  region: REGION
});

export const redirectUri = `${window.location.origin}/saml/callback`;

/** Legacy exchange only: the backend path keeps its tokens out of storage. */
const storeSsoTokens = (idToken: string, accessToken: string) => {
  localStorage.setItem("id_token", idToken);
  localStorage.setItem("token", idToken);
  localStorage.setItem("access_token", accessToken);
  localStorage.setItem("auth_method", "sso");
};

/** sessionStorage key for one-shot SAML retry after Cognito cookie is established */
const SSO_SAML_RETRY_SESSION_KEY = "sso_saml_retry";

export function AuthCallback() {
  const navigate = useNavigate();
  const { tenantProfile, setUserSession } = useApp();
  const isCalled = useRef(false);
  const [showCohortForm, setShowCohortForm] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");
  const errorDescription = params.get("error_description");

  useEffect(() => {
    if (!tenantProfile) {
      return;
    }

    // Handle Cognito SAML errors (e.g., "Invalid samlResponse or relayState")
    // This occurs on clean sessions due to browser SameSite cookie restrictions
    // on the cross-origin POST from the IdP back to Cognito's SAML endpoint.
    // The first attempt establishes the Cognito cookie, so a silent retry succeeds.
    if (error || errorDescription) {
      const hasRetried = sessionStorage.getItem(SSO_SAML_RETRY_SESSION_KEY);

      if (!hasRetried && tenantProfile?.sso_redirect_url) {
        sessionStorage.setItem(SSO_SAML_RETRY_SESSION_KEY, "true");
        window.location.href = tenantProfile.sso_redirect_url;
        return;
      } else {
        // Already retried once — clear flag and show error to avoid infinite loop
        sessionStorage.removeItem(SSO_SAML_RETRY_SESSION_KEY);
        ShowToast({
          message: "SSO authentication failed. Please try again.",
          type: "error",
        });
        navigate("/login", { replace: true });
        return;
      }
    }

    // Clear retry flag on successful auth (code received)
    sessionStorage.removeItem(SSO_SAML_RETRY_SESSION_KEY);

    if (code && !isCalled.current) {
      isCalled.current = true;
      exchangeCodeForToken(code);
    }
  }, [code, tenantProfile]);


  const navigateToDashboard = useNavigateToDashboard(async (userId) => {
    const userResponse = await api.searchUsers();
    return userResponse?.users?.find((user) => user.id === userId);
  });

  const handleCohortContinue = () => {
    setShowCohortForm(false);
    navigateToDashboard(false, undefined, () => setShowCohortForm(true));
  }

  // Nothing on this page makes an authenticated call, and /saml/callback is
  // public, so no 401 will redirect for us — a failure here has to do it itself
  // or the user sits on the loading animation.
  const failSsoExchange = (err: unknown) => {
    getLoggerRef()?.warn("SSO exchange failed", {
      errorName: (err as Error)?.name,
      errorMessage: (err as Error)?.message,
    });
    ShowToast({
      message: "SSO authentication failed. Please try again.",
      type: "error",
    });
    navigate("/login", { replace: true });
  };

  const exchangeCodeViaBackend = async (code: string) => {
    try {
      const { idToken } = await authApi.ssoCallback(code, redirectUri);
      if (!idToken) {
        throw new Error("SSO callback returned no ID token");
      }
      // Throws before writing anything if the token cannot be decoded.
      const session = establishBackendSession(idToken, tenantProfile);
      // The refresh timer arms off this; nothing else publishes on this path.
      setUserSession(session);
      return { userId: session.idToken.payload?.sub as string | undefined };
    } catch (err) {
      failSsoExchange(err);
    }
  };

  const exchangeCodeForToken = async (code) => {
    if (isBackendAuthEnabled()) {
      const result = await exchangeCodeViaBackend(code);
      if (result) {
        // Never throws: a failed cohort lookup falls through to the dashboard.
        navigateToDashboard(true, result.userId, () => setShowCohortForm(true));
      }
      return;
    }

    const cognitoDomain = tenantProfile?.user_pool_cognito_domain;
    if (!redirectUri || !cognitoDomain || !tenantProfile?.sso_client_id) {
      return;
    }
    try {
      const response = await fetch(`${cognitoDomain}/oauth2/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "authorization_code",
          client_id: tenantProfile?.sso_client_id,
          redirect_uri: redirectUri,
          code,
        }),
      });
      const data = await response.json();
      if (data.id_token) {
        storeSsoTokens(data.id_token, data.access_token);
        setRefreshToken(data.refresh_token);

        getCognitoUserSession(data.id_token, data.access_token, data.refresh_token);
      }
    } catch (error) {
      ShowGenericErrorToast({ supportLink: false });
    }
  };

  /** Legacy exchange only: loads the user's attributes, then hands off to the dashboard. */
  const loadUserFromCognito = (accessToken: string) => {
    if (!tenantProfile) {
      return;
    }
    AWS.config.update({
      region: REGION,
    } as any);
    const params = {
      AccessToken: accessToken,
    };
    cognito.getUser(params, (err, data) => {
      if (err) {
        // Tokens were stored before this lookup; don't leave them for the auth gate.
        clearLegacyCognitoSession();
        localStorage.removeItem("auth_method");
        failSsoExchange(err);
      } else {
        const attributes = data?.UserAttributes || [];
        const user: any = {};
        attributes.forEach((attribute: any) => {
          user[attribute.Name] = attribute.Value;
        });
        setUser(user);
        navigateToDashboard(true, user.sub, () => setShowCohortForm(true));
      }
    });
  };

  const getCognitoUserSession = (idToken?: string, accessToken?: string, refreshToken?: string) => {
    if (idToken && accessToken && refreshToken) {
      loadUserFromCognito(accessToken);
    }
  };

  return (
    <div className="md:self-center md:flex-1 h-[calc(100vh-118px)] h-auto overflow-auto w-full bg-white flex justify-center items-center">
      <div className="w-full h-full flex justify-center items-center">
        <Lottie
          animationData={animationData}
          loop={true}
          className={`w-[175px] h-[175px]`}
        />
      </div>
      <Modal isOpen={showCohortForm} title="Select your cohort to continue" contentClassName="[&_.title-container]:!px-0">
        <div className="mt-4">
          <CohortForm onContinue={handleCohortContinue} />
        </div>
      </Modal>
    </div>
  );
}